import * as React from 'react';
import {ChecklistFormMode, ChecklistFormProperty, isRentalMode, isReturnalMode, PropertyValues} from "./dtos";
import ChecklistPropertyItem from "./ChecklistPropertyItem";
import locales from "../locales";
import {FormComponentProps} from "antd/lib/form";
import {checklistPropertyNameMap, returnalExtraPropertyNameMap} from "./send/ChecklistTypes";
import {Checklist, ChecklistProperty, ChecklistState} from "../api/dtos/dtos";
import TextArea from "antd/lib/input/TextArea";
import VerticalBaseFormItem from "../common/form-items/VerticalBaseFormItem";
import ChecklistItemSection from "./layout/ChecklistItemSection";
import ChecklistItemLayout from "./layout/ChecklistItemLayout";


interface Props extends FormComponentProps {
  rentalChecklist?: Checklist;
  returnalChecklist?: Checklist;
  editable: boolean;
  mode: ChecklistFormMode;
}

interface State {
  properties: ChecklistFormProperty[];
}


const FIELD_COMMENT = 'comment';

class ChecklistFormContent extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const rentalPropertyMap: Map<string, PropertyValues> = this.mapPropertyValues(props.rentalChecklist?.properties);
    const returnalPropertyMap: Map<string, PropertyValues> = this.mapPropertyValues(props.returnalChecklist?.properties);

    const properties: ChecklistFormProperty[] = [];
    checklistPropertyNameMap.forEach((typedProperty, key) => {
      properties.push({
        key,
        type: typedProperty.type,
        label: typedProperty.name,
        rental: props.rentalChecklist?.state !== ChecklistState.IGNORED ? rentalPropertyMap.get(key) : undefined,
        returnal: props.returnalChecklist?.state !== ChecklistState.IGNORED ? returnalPropertyMap.get(key) : undefined,
        rentalEditable: (props.mode === ChecklistFormMode.CREATE_RENTAL) || (props.mode === ChecklistFormMode.EDIT_RENTAL && props.editable),
        returnalEditable: (props.mode === ChecklistFormMode.CREATE_RETURNAL) || (props.mode === ChecklistFormMode.EDIT_RETURNAL && props.editable),
      });
    });

    if (props.returnalChecklist || props.mode === ChecklistFormMode.CREATE_RETURNAL) {
      returnalExtraPropertyNameMap.forEach((typedProperty, key) => {
        properties.push({
          key,
          type: typedProperty.type,
          label: typedProperty.name,
          rental: undefined,
          returnal: props.returnalChecklist?.state !== ChecklistState.IGNORED ? returnalPropertyMap.get(key) : undefined,
          rentalEditable: false,
          returnalEditable: (props.mode === ChecklistFormMode.CREATE_RETURNAL) || (props.mode === ChecklistFormMode.EDIT_RETURNAL && props.editable),
        });
      });

    }

    this.state = {
      properties,
    };
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const {properties} = this.state;
    const {mode, returnalChecklist, rentalChecklist, editable} = this.props;

    let ps = properties;

    if (isRentalMode(prevProps.mode) && isReturnalMode(mode)) {
      returnalExtraPropertyNameMap.forEach((typedProperty, key) => {
        properties.push({
          key,
          type: typedProperty.type,
          label: typedProperty.name,
          rental: undefined,
          returnal: undefined,
          rentalEditable: false,
          returnalEditable: (mode === ChecklistFormMode.CREATE_RETURNAL) || (mode === ChecklistFormMode.EDIT_RETURNAL && editable),
        });
      });
    }

    if (prevProps.rentalChecklist !== rentalChecklist) {
      const rentalPropertyMap: Map<string, PropertyValues> = this.mapPropertyValues(rentalChecklist?.properties);

      ps = ps.map(property => {
        const rental = rentalPropertyMap.get(property.key);
        return {
          ...property,
          rental: rentalChecklist?.state !== ChecklistState.IGNORED ? rental : undefined,
        };
      })

    }
    if (prevProps.returnalChecklist !== returnalChecklist) {
      const returnalPropertyMap = this.mapPropertyValues(returnalChecklist?.properties);

      ps = ps.map(property => {
        return {
          ...property,
          returnal: returnalChecklist?.state !== ChecklistState.IGNORED ? returnalPropertyMap.get(property.key) : undefined,
        };
      })

    }

    if (prevProps.editable !== this.props.editable) {
      ps = ps.map(property => {
        return {
          ...property,
          rentalEditable: (mode === ChecklistFormMode.CREATE_RENTAL) || (mode === ChecklistFormMode.EDIT_RENTAL && editable),
          returnalEditable: (mode === ChecklistFormMode.CREATE_RETURNAL) || (mode === ChecklistFormMode.EDIT_RETURNAL && editable),
        };
      })
    }

    if (ps !== properties) {
      this.setState({properties: ps});
    }
  }

  public render(): React.ReactNode {
    const {mode, form} = this.props;
    const {properties} = this.state;

    return (
      <>
        {properties && properties.map((property, index) => {
          const num = index + 1;
          return (
            <ChecklistItemSection
              key={property.key}
              title={`${num}. ${property.label}`}
            >
              <ChecklistPropertyItem mode={mode} property={property} form={form}/>
            </ChecklistItemSection>
          );
        })}
        {this.renderComment()}
      </>
    );
  }

  private renderComment = () => {
    const {mode, form, editable, returnalChecklist, rentalChecklist} = this.props;
    const rentalComment = rentalChecklist?.comment;
    const returnalComment = returnalChecklist?.comment;
    const rentalCommentEditable = (mode === ChecklistFormMode.CREATE_RENTAL || mode === ChecklistFormMode.EDIT_RENTAL);
    const returnalCommentEditable = (mode === ChecklistFormMode.CREATE_RETURNAL || mode === ChecklistFormMode.EDIT_RETURNAL);
    const showRentalLabel = (returnalChecklist !== undefined || mode === ChecklistFormMode.CREATE_RETURNAL);
    const showReturnalLabel = ((returnalChecklist !== undefined || mode === ChecklistFormMode.CREATE_RETURNAL) && rentalChecklist !== undefined);

    return (
      <ChecklistItemLayout
        style={{marginTop: 48}}
        rentalLabel={locales.rental_other_comment}
        returnalLabel={locales.returnal_other_comment}
        showRentalLabel={showRentalLabel}
        showReturnalLabel={showReturnalLabel}
        rentalChild={((rentalChecklist && rentalChecklist.state !== ChecklistState.IGNORED) || mode === ChecklistFormMode.CREATE_RENTAL) &&
            <VerticalBaseFormItem
                form={form}
                label={!showRentalLabel ? locales.rental_other_comment : undefined}
                fieldId={FIELD_COMMENT}
                initialValue={rentalComment}
                required={false}
                input={
                  rentalCommentEditable ?
                    <TextArea
                      maxLength={255}
                      disabled={!editable}
                    /> :
                    <span style={{lineHeight: 1.5}} className="ant-form-text">{rentalComment ?? '-'}</span>
                }
            />
        }
        returnalChild={(returnalChecklist || mode === ChecklistFormMode.CREATE_RETURNAL) &&
            <VerticalBaseFormItem
                form={form}
                fieldId={FIELD_COMMENT}
                label={!showReturnalLabel ? locales.returnal_other_comment : undefined}
                initialValue={returnalComment}
                required={false}
                input={
                  returnalCommentEditable ?
                    <TextArea
                      maxLength={255}
                      disabled={!editable}
                    /> :
                    <span style={{lineHeight: 1.5}} className="ant-form-text">{returnalComment ?? '-'}</span>
                }
            />
        }
      />
    );
  }

  private mapPropertyValues = (properties?: ChecklistProperty[]): Map<string, PropertyValues> => {
    return new Map(properties?.map((p): [string, PropertyValues] =>
      [p.name,
        {
          value: p.value,
          comment: p.comment
        },
      ]
    ))
  }
}

export default ChecklistFormContent;




// WEBPACK FOOTER //
// ./src/checklist/ChecklistFormContent.tsx